// ts-rest contract for ops-only operational endpoints (ADR-0006, issue #92).
//
// Surfaces the "manually fire a detector" affordance that used to live
// under `/api/demo/run-detector/:agent`. The endpoint is genuinely
// useful in production for re-running a detector after a config change
// or after backfilling source data — but it is *not* a demo affordance
// in a no-seed-data world, so it moves to `/api/ops/detectors/:agent/run`,
// persona-gated to `ops`/`comply` plus each detector agent's declared
// user-trigger personas (desk for WARDEN/QUORUM), and audited per invocation.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { runDetectorResponseSchema, staffActionOkSchema, staffActionErrorSchema } from './agent';

const c = initContract();

export const sftpIngestFailureSchema = z.object({
    id: z.string(),
    at: z.string().nullable().optional(),
    summary: z.string().nullable().optional(),
    meta: z
        .object({
            path: z.string().optional(),
            quarantinePath: z.string(),
            counterparty: z.string(),
            fileType: z.string(),
            scope: z.string(),
            error: z.string(),
            resolvedAt: z.string().nullable().optional(),
            resolvedBy: z.string().nullable().optional(),
            resolutionNote: z.string().nullable().optional(),
        })
        .passthrough(),
});

// ---------------------------------------------------------------------------
// Trade DTO — the Trade Operations page projection of the `Trade` Object Type
// (settlement + blotter tabs read the same rows). Tight per ADR-0018: every
// field is a real leaf, no `z.unknown`. Nullable fields carry the Order
// Blotter denormalised values that the feed may omit.
// ---------------------------------------------------------------------------

export const opsTradeStatusSchema = z.enum([
    'Decision',
    'Pending',
    'PendingConfirmation',
    'Confirmed',
    'Instructed',
    'Settling',
    'Settled',
    'AtRisk',
    'Failed',
    'Cancelled',
]);
export type OpsTradeStatus = z.infer<typeof opsTradeStatusSchema>;

export const opsTradeDtoSchema = z.object({
    id: z.string(),
    tradeRef: z.string(),
    side: z.enum(['Buy', 'Sell']),
    status: opsTradeStatusSchema,
    quantity: z.number(),
    price: z.number(),
    consideration: z.number(),
    tradeValue: z.number().nullable(),
    currency: z.string().nullable(),
    charges: z.number().nullable(),
    commission: z.number().nullable(),
    // Security identity (display strings; links not resolved at ingest).
    securityCode: z.string().nullable(),
    securityName: z.string().nullable(),
    isin: z.string().nullable(),
    securityClass: z.string().nullable(),
    // Holding fund.
    fundName: z.string().nullable(),
    registration: z.string().nullable(),
    // Dealing desk.
    counterparty: z.string().nullable(),
    market: z.string().nullable(),
    dealer: z.string().nullable(),
    tradedBy: z.string().nullable(),
    custodian: z.string().nullable(),
    risk: z.string(),
    // Lifecycle dates (ISO yyyy-mm-dd).
    tradeDate: z.string(),
    orderDate: z.string().nullable(),
    settlementDate: z.string(),
    instructedOn: z.string().nullable(),
});
export type OpsTradeDto = z.infer<typeof opsTradeDtoSchema>;

export const listTradesResponseSchema = z.object({ rows: z.array(opsTradeDtoSchema) });
export type ListTradesResponse = z.infer<typeof listTradesResponseSchema>;

export const opsContract = c.router(
    {
        listTrades: {
            method: 'GET',
            path: '/ops/trades',
            responses: {
                200: listTradesResponseSchema,
                403: staffActionErrorSchema,
            },
            summary: 'List Trades for the Trade Operations page (Settlement + Blotter tabs).',
        },
        runDetector: {
            method: 'POST',
            path: '/ops/detectors/:agent/run',
            pathParams: z.object({ agent: z.string() }),
            body: z
                .object({
                    frontend: z.enum(['desk', 'ops', 'comply', 'view', 'global']).optional(),
                })
                .passthrough()
                .optional(),
            responses: {
                200: runDetectorResponseSchema,
                400: z.object({ ok: z.boolean(), error: z.string() }),
                403: z.object({ ok: z.boolean(), error: z.string() }),
            },
            summary:
                "Manually fire a detector AgentTask (persona-gated to ops/comply plus the agent's declared user-trigger personas, audited).",
        },
        listSftpIngestFailures: {
            method: 'GET',
            path: '/ops/sftp-ingest/failures',
            responses: {
                200: z.object({ failures: z.array(sftpIngestFailureSchema) }),
                403: z.object({ ok: z.boolean(), error: z.string() }),
            },
            summary: 'List unresolved SFTP ingest failures.',
        },
        resolveSftpIngestFailure: {
            method: 'POST',
            path: '/ops/sftp-ingest/failures/:id/resolve',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ resolutionNote: z.string().optional() }).optional(),
            responses: {
                200: z.object({ ok: z.literal(true), failure: sftpIngestFailureSchema }),
                403: z.object({ ok: z.boolean(), error: z.string() }),
                404: z.object({ ok: z.boolean(), error: z.string() }),
            },
            summary: 'Mark an SFTP ingest failure resolved.',
        },
        retrySftpIngestFailure: {
            method: 'POST',
            path: '/ops/sftp-ingest/failures/:id/retry',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ resolutionNote: z.string().optional() }).optional(),
            responses: {
                200: z.object({ ok: z.literal(true), failure: sftpIngestFailureSchema }),
                400: z.object({ ok: z.boolean(), error: z.string() }),
                403: z.object({ ok: z.boolean(), error: z.string() }),
                404: z.object({ ok: z.boolean(), error: z.string() }),
            },
            summary: 'Retry a quarantined SFTP ingest failure and resolve it on success.',
        },

        // -----------------------------------------------------------------
        // Ops mutations (OLD_CODE.html #2 replacement for the named-action
        // handlers previously in apps/server/src/lib/db/actions/ops.ts).
        // All endpoints are persona-gated to `ops`/`admin` at the controller
        // and stamp actor attribution on the audit log.
        // -----------------------------------------------------------------
        // These mutations dispatch the registered ontology Actions (ADR-0008).
        // The `:id` path param identifies the Object Type row that carries the
        // lifecycle: a Trade for trade endpoints, the CashAlert (NOT the cash
        // *position*) for cash endpoints, the CoacAction for corporate-action
        // endpoints, and the Fee for fee endpoints. Bodies carry the real
        // workflow inputs the target Action requires (reason / note); the
        // optional refs are pure annotations recorded in the audit envelope.
        retryTrade: {
            method: 'POST',
            path: '/ops/trades/:id/retry',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ retryReason: z.string().optional() }).optional(),
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Retry a Failed trade (status Failed → Pending).',
        },
        confirmTrade: {
            method: 'POST',
            path: '/ops/trades/:id/confirm',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ confirmationRef: z.string().optional() }).optional(),
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Confirm a trade (status PendingConfirmation → Confirmed).',
        },
        cancelTrade: {
            method: 'POST',
            path: '/ops/trades/:id/cancel',
            pathParams: z.object({ id: z.string() }),
            // cancellationReason is a material workflow decision the CancelTrade
            // Action requires (non-empty) — collected here, never faked server-side.
            body: z.object({ cancellationReason: z.string().min(1) }),
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Cancel a trade before settlement (Settling deliberately excluded).',
        },
        acknowledgeCashAlert: {
            method: 'POST',
            path: '/ops/cash-positions/:id/acknowledge',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ acknowledgementNote: z.string().optional() }).optional(),
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Acknowledge a CashAlert (`:id` is the CashAlert id).',
        },
        escalateCashPosition: {
            method: 'POST',
            path: '/ops/cash-positions/:id/escalate',
            pathParams: z.object({ id: z.string() }),
            // escalationReason is a material workflow decision the EscalateCashAlert
            // Action requires (non-empty) — collected here, never faked server-side.
            body: z.object({ reason: z.string().min(1) }),
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Escalate a CashAlert (`:id` is the CashAlert id).',
        },
        approveCaElection: {
            method: 'POST',
            path: '/ops/corporate-actions/:id/approve-election',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ approvalNote: z.string().optional() }).optional(),
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Approve a CoacAction election (`:id` is the CoacAction id).',
        },
        defaultCaElection: {
            method: 'POST',
            path: '/ops/corporate-actions/:id/default-election',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ defaultReason: z.string().optional() }).optional(),
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Accept the default CoacAction election (`:id` is the CoacAction id).',
        },
        routeCaToIm: {
            method: 'POST',
            path: '/ops/corporate-actions/:id/route-to-im',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ note: z.string().optional() }).optional(),
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Route a CoacAction to the IM for review (`:id` is the CoacAction id).',
        },
        approveFee: {
            method: 'POST',
            path: '/ops/fees/:id/approve',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ approvalNote: z.string().optional() }).optional(),
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Approve an outstanding fee (Accrued | Queried → Approved).',
        },
        queryFee: {
            method: 'POST',
            path: '/ops/fees/:id/query',
            pathParams: z.object({ id: z.string() }),
            // queryNote is the workflow content the QueryFee Action requires
            // (non-empty) — the fees page already collects it via its query modal.
            body: z.object({ note: z.string().min(1) }),
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Raise a query on an outstanding fee (Accrued | Queried → Queried).',
        },
        markFeePaid: {
            method: 'POST',
            path: '/ops/fees/:id/mark-paid',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ paymentRef: z.string().optional() }).optional(),
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Mark an outstanding fee paid (Approved → Processed).',
        },
        testIntegrationConnection: {
            method: 'POST',
            path: '/ops/integrations/:systemId/test',
            pathParams: z.object({ systemId: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Test the connection to an integrated system.',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
